import type { Enrollment, MembershipItem } from '@spike/membership-model';
import {
    computeMembershipDisplayPrice,
    computeMembershipPreviewDiscount,
    getMembershipRemainingUses,
    isEnrollmentAvailableOnDate,
    resolveMembershipBasePrice,
    shouldApplyMembershipToAllAppointments
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import { Transportation, DataVaraible, PetServiceWrapper } from 'model/NewMultipleBooking';
import moment, { Moment } from 'moment-timezone';
import { getTransportationDateSlotsForSave, TransportationLegType } from 'utils/transportationDateSlots';

const enrollmentFromTransportation = (transportation: Transportation): Enrollment | undefined =>
    transportation.enrollment;

const membershipItemFromTransportation = (transportation: Transportation): MembershipItem | undefined =>
    transportation.membershipItem;

const buildBaseTransportationService = (
    transportation: Transportation,
    isPickup: boolean,
    catalogPriceOverride?: number
): {
    service: PetServiceWrapper;
    catalogPrice: number;
    slotLinePrice: number;
    manualDiscount: { type: 'amount' | 'percentage'; value: number };
} => {
    const transportationData = isPickup ? transportation.pickup : transportation.dropOff;
    const durationInMinutes = moment(isPickup ? transportation.pickup?.time.to : transportation.dropOff?.time.to).diff(
        moment(isPickup ? transportation.pickup?.time.from : transportation.dropOff?.time.from),
        'minutes'
    );
    const defaultPrice = isPickup
        ? transportation.service?.pickupPrice || 0
        : transportation.service?.dropoffPrice || 0;
    const catalogPrice = catalogPriceOverride ?? defaultPrice;
    const slotLinePrice = transportationData?.price ?? defaultPrice;
    const manualDiscount = {
        type: (transportationData?.discount?.type || 'amount') as 'amount' | 'percentage',
        value: transportationData?.discount?.value ?? 0
    };
    const saveCustomPrice =
        transportation.transportType === 'roundtrip'
            ? !!(transportation.pickup?.saveCustomPrice || transportation.dropOff?.saveCustomPrice)
            : !!transportationData?.saveCustomPrice;

    return {
        catalogPrice,
        slotLinePrice,
        manualDiscount,
        service: {
            service: {
                id: transportation.service?.serviceId || 0,
                name: transportation.service?.serviceName || 'Transportation Service'
            },
            price: slotLinePrice,
            beforePrice: catalogPrice > 0 ? catalogPrice : defaultPrice,
            durationInMinutes: durationInMinutes || 0,
            duration: {
                hours: durationInMinutes ? Math.trunc(durationInMinutes / 60) : 0,
                minutes: durationInMinutes ? durationInMinutes % 60 : 0
            },
            discount: manualDiscount,
            businessArea: 'transportation',
            color: transportation.service?.color ?? '',
            vehicle: transportation.vehicle,
            address: isPickup ? transportation.pickup?.address || '' : transportation.dropOff?.address || '',
            instructions: isPickup
                ? transportation.pickup?.instructions || ''
                : transportation.dropOff?.instructions || '',
            saveCustomPrice,
            customPriceId: transportationData?.customPriceId,
            customPriceAmount: slotLinePrice > 0 ? slotLinePrice : undefined
        }
    };
};

export const buildTransportationVariableDataPerSlot = (
    transportation: Transportation,
    fallbackDate: Moment,
    timezone: string,
    isEditMode = false,
    membershipUsesAlreadyAllocated = 0,
    editLegType?: TransportationLegType,
    catalogPriceOverrides?: { pickup?: number; dropoff?: number }
): Array<DataVaraible> => {
    const transportationEnrollment = enrollmentFromTransportation(transportation);
    const membershipItem = membershipItemFromTransportation(transportation);
    const slots = getTransportationDateSlotsForSave(transportation, fallbackDate, timezone, isEditMode, editLegType);

    const applyMembershipToAll =
        transportationEnrollment != null &&
        membershipItem != null &&
        shouldApplyMembershipToAllAppointments(membershipItem, transportationEnrollment);
    let limitedMembershipUsesRemaining =
        transportationEnrollment != null && membershipItem != null && !applyMembershipToAll
            ? Math.max(
                  0,
                  getMembershipRemainingUses(membershipItem, transportationEnrollment) - membershipUsesAlreadyAllocated
              )
            : Number.POSITIVE_INFINITY;
    // Roundtrip creates 2 legs/day; consume 1 limited use per day and apply to both legs.
    const limitedMembershipDaysApplied = new Set<string>();

    return slots.map(slot => {
        const isPickup = slot.type === 'pickup';
        const {
            service: baseService,
            catalogPrice,
            slotLinePrice,
            manualDiscount
        } = buildBaseTransportationService(
            transportation,
            isPickup,
            isPickup ? catalogPriceOverrides?.pickup : catalogPriceOverrides?.dropoff
        );

        const membershipAvailableOnDate =
            transportationEnrollment != null &&
            membershipItem != null &&
            isEnrollmentAvailableOnDate(transportationEnrollment, slot.date, timezone);
        const dayKey = slot.date.clone().tz(timezone).startOf('day').format('YYYY-MM-DD');
        let applyMembership = false;

        if (membershipAvailableOnDate) {
            if (applyMembershipToAll) {
                applyMembership = true;
            } else if (limitedMembershipDaysApplied.has(dayKey)) {
                applyMembership = true;
            } else if (limitedMembershipUsesRemaining > 0) {
                applyMembership = true;
                limitedMembershipDaysApplied.add(dayKey);
                limitedMembershipUsesRemaining -= 1;
            }
        }

        const serviceForSlot: PetServiceWrapper = applyMembership
            ? (() => {
                  const membershipBase = resolveMembershipBasePrice(catalogPrice, slotLinePrice);
                  const membershipPricing = computeMembershipDisplayPrice(membershipBase, membershipItem);
                  const previewDiscount = computeMembershipPreviewDiscount(membershipBase, membershipItem);
                  return {
                      ...baseService,
                      price: membershipPricing.displayPrice,
                      beforePrice: membershipBase,
                      customPriceAmount: membershipBase > 0 ? membershipBase : undefined,
                      discount: previewDiscount
                          ? { type: previewDiscount.type, value: previewDiscount.value }
                          : { type: 'amount' as const, value: 0 },
                      enrollment: transportationEnrollment,
                      membershipItem
                  };
              })()
            : {
                  ...baseService,
                  price: slotLinePrice,
                  beforePrice: catalogPrice > 0 ? catalogPrice : baseService.beforePrice,
                  discount: { type: 'amount' as const, value: 0 },
                  enrollment: undefined,
                  membershipItem: undefined
              };

        const discountForSlot =
            transportationEnrollment != null && membershipItem != null && !applyMembership
                ? { type: 'amount' as const, value: 0 }
                : applyMembership
                ? (() => {
                      const membershipBase = resolveMembershipBasePrice(catalogPrice, slotLinePrice);
                      const previewDiscount = computeMembershipPreviewDiscount(membershipBase, membershipItem);
                      return previewDiscount
                          ? {
                                type: previewDiscount.type as 'amount' | 'percentage',
                                value: previewDiscount.value
                            }
                          : { type: 'percentage' as const, value: 100 };
                  })()
                : manualDiscount;

        return {
            service: serviceForSlot,
            staffId: transportation.selectedStaff?.id,
            date: slot.date,
            dateEnd: slot.dateEnd,
            itemData: {
                discount: discountForSlot,
                flow: 'transportation',
                type: slot.type,
                ...(catalogPrice > 0 || slotLinePrice > 0
                    ? { price: resolveMembershipBasePrice(catalogPrice, slotLinePrice) }
                    : {})
            }
        };
    });
};
